import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { RefreshCw, Filter, X, Loader2 } from "lucide-react";
import { useReferralFunnel } from "@/hooks/useReferralFunnel";
import { ReferralFunnelMetrics } from "./components/ReferralFunnelMetrics";
import { ReferralsByLaunchTable } from "./components/ReferralsByLaunchTable";
import { ReferralDetailGrouped } from "./components/ReferralDetailGrouped";

export default function ReferralsFunnel() {
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [selectedLaunchId, setSelectedLaunchId] = useState<number | undefined>();
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState("launches");

  const {
    totalMainSubmissions,
    totalInvited,
    totalResponded,
    conversionRate,
    byLaunch,
    details,
    isLoading,
    error,
    refetch,
  } = useReferralFunnel({
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    launchId: selectedLaunchId,
  });

  const handleClearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setSelectedLaunchId(undefined);
  };

  const handleSelectLaunch = (launchId: number) => {
    setSelectedLaunchId(launchId);
    setActiveTab("details");
  };

  const hasFilters = dateFrom || dateTo || selectedLaunchId;

  if (error) {
    return (
      <div className="p-6">
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-800">Error al cargar datos</CardTitle>
            <CardDescription className="text-red-600">
              {error.message}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Reintentar
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Embudo de Referidos</h1>
          <p className="text-muted-foreground">
            Seguimiento de invitaciones y respuestas de referidos
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="mr-2 h-4 w-4" />
            Filtros
            {hasFilters && (
              <span className="ml-2 rounded-full bg-primary px-2 py-0.5 text-xs text-primary-foreground">
                {[dateFrom, dateTo, selectedLaunchId].filter(Boolean).length}
              </span>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Filtros */}
      {showFilters && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">Filtros</CardTitle>
              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                  <X className="mr-2 h-4 w-4" />
                  Limpiar
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="dateFrom">Fecha desde</Label>
                <Input
                  id="dateFrom"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dateTo">Fecha hasta</Label>
                <Input
                  id="dateTo"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Lanzamiento</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  value={selectedLaunchId || ""}
                  onChange={(e) =>
                    setSelectedLaunchId(e.target.value ? Number(e.target.value) : undefined)
                  }
                >
                  <option value="">Todos los lanzamientos</option>
                  {byLaunch.map((launch) => (
                    <option key={launch.launchId} value={launch.launchId}>
                      {launch.launchName} ({launch.clientName})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading */}
      {isLoading ? (
        <div className="space-y-6">
          {/* Skeleton Métricas */}
          <div className="grid gap-4 md:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <Card key={i}>
                <CardHeader className="pb-2">
                  <Skeleton className="h-4 w-28" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-8 w-20" />
                </CardContent>
              </Card>
            ))}
          </div>
          {/* Skeleton Tabs */}
          <div className="space-y-4">
            <Skeleton className="h-10 w-80" />
            <Card>
              <CardContent className="p-4">
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="flex gap-4">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-4 w-48" />
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      ) : (
        <>
          {/* Métricas */}
          <ReferralFunnelMetrics
            totalMainSubmissions={totalMainSubmissions}
            totalInvited={totalInvited}
            totalResponded={totalResponded}
            conversionRate={conversionRate}
          />

          {/* Tabs con vistas - Controlado */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList>
              <TabsTrigger value="launches">Por Lanzamiento</TabsTrigger>
              <TabsTrigger value="details">Detalle por Persona</TabsTrigger>
            </TabsList>

            <TabsContent value="launches" className="space-y-4">
              <ReferralsByLaunchTable
                data={byLaunch}
                onSelectLaunch={handleSelectLaunch}
              />
            </TabsContent>

            <TabsContent value="details" className="space-y-4">
              <ReferralDetailGrouped
                data={details}
                selectedLaunchId={selectedLaunchId}
                onClearLaunchFilter={() => setSelectedLaunchId(undefined)}
              />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
